import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ShoppingBag, Shield, Truck, Star } from 'lucide-react';
import { heroSlides } from '../data/mock';

const HeroCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const nextSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const prevSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  useEffect(() => {
    const interval = setInterval(nextSlide, 6000);
    return () => clearInterval(interval);
  }, [nextSlide]);

  const slide = heroSlides[currentSlide];
  const featureIcons = [Shield, Truck, Star];

  return (
    <section className="relative overflow-hidden" data-testid="hero-carousel">
      <div className={`relative min-h-[380px] md:min-h-[420px] bg-gradient-to-r ${slide.bgColor} transition-all duration-700`}>
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-20 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 h-full relative z-10">
          <div className="grid md:grid-cols-2 gap-8 items-center min-h-[380px] md:min-h-[420px]">
            {/* Left - Content */}
            <div className="text-white py-10 md:py-16">
              {/* Discount badge */}
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/20 backdrop-blur-sm rounded-full text-sm font-semibold mb-4 animate-[fadeInUp_0.5s_ease-out]">
                <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
                {slide.discount}
              </div>

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4 animate-[fadeInUp_0.6s_ease-out]">
                {slide.title}
              </h1>
              <p className="text-white/80 text-base md:text-lg mb-6 max-w-lg animate-[fadeInUp_0.7s_ease-out]">
                {slide.subtitle}
              </p>

              {/* Feature pills */}
              <div className="flex flex-wrap gap-2 mb-8 animate-[fadeInUp_0.8s_ease-out]">
                {slide.features.map((feature, i) => {
                  const Icon = featureIcons[i % featureIcons.length];
                  return (
                    <span key={i} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 backdrop-blur-sm rounded-full text-sm">
                      <Icon className="w-3.5 h-3.5" />
                      {feature}
                    </span>
                  );
                })}
              </div>

              <button
                onClick={() => {
                  const cats = document.querySelector('[data-testid="browse-categories-section"]');
                  if (cats) cats.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-gray-800 font-bold rounded-xl hover:bg-gray-100 hover:scale-[1.02] transition-all shadow-lg group"
                data-testid="hero-shop-now"
              >
                <ShoppingBag className="w-5 h-5 group-hover:scale-110 transition-transform" />
                Shop Now
              </button>
            </div>

            {/* Right - Image */}
            <div className="hidden md:flex items-center justify-center">
              <div className="relative w-80 h-80">
                <div className="absolute inset-0 bg-white/10 rounded-3xl rotate-6" />
                <div className="absolute inset-0 bg-white/5 rounded-3xl -rotate-3" />
                <img
                  src={slide.image}
                  alt={slide.title}
                  className="relative z-10 w-full h-full object-cover rounded-3xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors z-20"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors z-20"
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        {/* Dots */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'w-8 bg-white' : 'w-2 bg-white/40 hover:bg-white/60'
              }`}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};

export default HeroCarousel;
